import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { TextKey, SearchType } from '@mews-commander/enums';
import { ApplicationState, SearchResult, WithServiceId, Option, CommanderThunkDispatch } from '@mews-commander/types';
import { FullWidthFieldsContainer, LocalizedSectionTitle, ReadOnly, FormMetaSection } from '@mews-commander/ui';
import { Stack } from '@optimus-web/core';
import { LocalizedEnum, LocalizedText } from '@mews/framework';
import { TimeUnit, UnicodeEntity } from '@mews/enums';
import { AvailabilityBlockState, PickupDistribution, ReservationPurpose } from '@mews-commander/data-access';
import { getLocalizedServiceName, getServiceTimeUnit } from '@mews-commander/legacy-entities/src/ducks/services';
import { getBookableServiceRateOptions, getServiceRateOptions } from '@mews-commander/legacy-entities/src/ducks/rates';
import { isCurrentEnterpriseAvailabilityBlockAdvancedFeaturesEnabled } from '@mews-commander/legacy-entities/src/selectors/session/subscription';
import {
    isCurrentProfileEnabledToManageCustomers,
    isCurrentProfileEnabledToManageCompanies,
} from '@mews-commander/legacy-entities/src/selectors/session/scope';
import {
    getAvailabilityBlock,
    getAvailabilityBlockFormattedMeta,
} from '@mews-commander/legacy-entities/src/ducks/availabilityBlocks';
import { useCommanderSelector } from '@mews-commander/utils';
import {
    EnumSelectField,
    InputField,
    SelectField,
    SelectionCardGroupField,
    TextareaField,
    TimeUnitIntervalPickerFields,
} from '@mews-commander/legacy-form';
import { SearchFormField } from '../../../components/form';
import { createNewSearchFormEntity, enhanceCustomerCreateUrl } from '../../search/actions/utils';
import { getBookerScope, getCompanyScope, getTravelAgencyScope } from '../utils';
import { FormValues } from '../types';
import { getMinEndDate } from '../selectors';
import { ReleaseDateField } from './ReleaseDateField';

export interface AvailabilityBlocksFormFieldsProps extends WithServiceId {
    form: string;
    availabilityBlockId?: string;
    initialValues?: FormValues;
}
interface StateProps {
    serviceName: string;
    timeUnit: TimeUnit;
    blockAdvancedFeaturesEnabled: boolean;
    rateOptions: Array<Option>;
    canCreateCustomers: boolean;
    canCreateCompanies: boolean;
    availabilityBlockMeta?: {
        creatorName: string;
        updaterName: string;
        createdDateTime: string;
        updatedDateTime: string;
    };
    availabilityBlockState: AvailabilityBlockState | null | undefined;
}
interface DispatchProps {
    createNewBooker: (searchResult: SearchResult) => void;
    createCompany: (searchResult: SearchResult) => void;
}
type Props = AvailabilityBlocksFormFieldsProps & StateProps & DispatchProps;

const AvailabilityBlockFormFields = ({
    serviceName,
    availabilityBlockId,
    blockAdvancedFeaturesEnabled,
    initialValues,
    form,
    rateOptions,
    canCreateCustomers,
    canCreateCompanies,
    createNewBooker,
    createCompany,
    availabilityBlockMeta,
    timeUnit,
    serviceId,
    availabilityBlockState,
}: Props) => {
    const minEndDate = useCommanderSelector((state) =>
        availabilityBlockId ? getMinEndDate(state, { form, id: availabilityBlockId, serviceId, timeUnit }) : null
    );
    const bookerScope = getBookerScope(form);
    const companyScope = getCompanyScope(form);
    const travelAgencyScope = getTravelAgencyScope(form);

    const enabledStates = blockAdvancedFeaturesEnabled
        ? [AvailabilityBlockState.Confirmed, AvailabilityBlockState.Optional, AvailabilityBlockState.Inquired]
        : [AvailabilityBlockState.Confirmed];

    const disableFieldForCanceledBlock = availabilityBlockState === AvailabilityBlockState.Canceled;
    const pickupDistributionOptions = [
        {
            label: <LocalizedEnum prefix="PickupDistribution" value={PickupDistribution.AllInOneGroup} />,
            name: PickupDistribution.AllInOneGroup,
            value: PickupDistribution.AllInOneGroup,
            dataTestId: PickupDistribution.AllInOneGroup,
        },
        {
            label: <LocalizedEnum prefix="PickupDistribution" value={PickupDistribution.IndividualGroups} />,
            name: PickupDistribution.IndividualGroups,
            value: PickupDistribution.IndividualGroups,
            dataTestId: PickupDistribution.IndividualGroups,
        },
    ];

    return (
        <Stack spacing="400" vertical>
            <FullWidthFieldsContainer>
                <LocalizedSectionTitle textKey={TextKey.Settings} />

                <ReadOnly name="service" label={<LocalizedText textKey={TextKey.Service} />} value={serviceName} />

                <InputField label={<LocalizedText textKey={TextKey.AvailabilityBlockName} />} name="name" required />

                <TimeUnitIntervalPickerFields
                    timeUnit={timeUnit}
                    required
                    endMinDate={minEndDate}
                    startName="startValue"
                    endName="endValue"
                    form={form}
                    disabled={disableFieldForCanceledBlock}
                />

                <SelectField
                    required
                    name="rateId"
                    options={rateOptions}
                    disabled={!!availabilityBlockId}
                    label={<LocalizedText textKey={availabilityBlockId ? TextKey.Rate : TextKey.OriginalRate} />}
                    bottomCaption={
                        !availabilityBlockId && (
                            <LocalizedText textKey={TextKey.AvailabilityBlockOriginalRateHelperText} />
                        )
                    }
                />

                {!disableFieldForCanceledBlock && (
                    <EnumSelectField
                        required
                        label={<LocalizedText textKey={TextKey.AvailabilityBlockState} />}
                        enumClass={AvailabilityBlockState}
                        name="state"
                        enumPrefix="AvailabilityBlockState"
                        enabledValues={enabledStates}
                    />
                )}
            </FullWidthFieldsContainer>

            <FullWidthFieldsContainer>
                <LocalizedSectionTitle textKey={TextKey.Rule} />

                <ReleaseDateField form={form} disabled={disableFieldForCanceledBlock} />

                {blockAdvancedFeaturesEnabled && (
                    <SelectionCardGroupField
                        name="pickupDistribution"
                        label={<LocalizedText textKey={TextKey.PickupDistribution} />}
                        caption={<LocalizedText textKey={TextKey.PickupDistributionHint} />}
                        helper={<LocalizedText textKey={TextKey.OptionCannotBeChangedLater} />}
                        selectOptionGroupType="radio"
                        templateColumns={`repeat(${pickupDistributionOptions.length}, 1fr)`}
                        required
                        disabled={!!availabilityBlockId}
                        options={pickupDistributionOptions}
                    />
                )}

                <InputField
                    label={<LocalizedText textKey={TextKey.ExternalIdentifier} />}
                    name="externalIdentifier"
                    helper={<LocalizedText textKey={TextKey.AvailabilityBlockExternalIdentifierTooltip} />}
                    disabled={!!availabilityBlockId}
                    placeholder={availabilityBlockId ? UnicodeEntity.Dash : undefined}
                />
            </FullWidthFieldsContainer>

            <FullWidthFieldsContainer>
                <LocalizedSectionTitle textKey={TextKey.Client} />

                <SearchFormField
                    scope={bookerScope}
                    searchScope={bookerScope}
                    preselectedResultId={initialValues?.bookerId}
                    newEntity={canCreateCustomers}
                    createNewEntity={createNewBooker}
                    label={<LocalizedText textKey={TextKey.Booker} />}
                    name="bookerId"
                    searchType={SearchType.Customer}
                    required
                />

                <SearchFormField
                    scope={companyScope}
                    searchScope={companyScope}
                    preselectedResultId={initialValues?.companyId}
                    newEntity={canCreateCompanies}
                    createNewEntity={createCompany}
                    label={<LocalizedText textKey={TextKey.Company} />}
                    name="companyId"
                    searchType={SearchType.Company}
                />

                <SearchFormField
                    scope={travelAgencyScope}
                    searchScope={travelAgencyScope}
                    preselectedResultId={initialValues?.travelAgencyId}
                    optionalSearchParams={{ serviceIds: [serviceId] }}
                    label={<LocalizedText textKey={TextKey.TravelAgency} />}
                    name="travelAgencyId"
                    searchType={SearchType.TravelAgency}
                />

                <EnumSelectField
                    label={<LocalizedText textKey={TextKey.ReservationPurpose} />}
                    name="reservationPurpose"
                    enumPrefix="ReservationPurpose"
                    enumClass={ReservationPurpose}
                    showEmptyValue
                />
            </FullWidthFieldsContainer>

            <FullWidthFieldsContainer>
                <LocalizedSectionTitle textKey={TextKey.Info} />

                <TextareaField label={<LocalizedText textKey={TextKey.Notes} />} name="notes" />

                <FormMetaSection meta={availabilityBlockMeta} />
            </FullWidthFieldsContainer>
        </Stack>
    );
};

export default connect<StateProps, DispatchProps, AvailabilityBlocksFormFieldsProps, ApplicationState>(
    // @ts-expect-error $TSFixMeError: No overload matches this call.
    createStructuredSelector<ApplicationState, AvailabilityBlocksFormFieldsProps, StateProps>({
        serviceName: (state, { serviceId }) => getLocalizedServiceName(state, { id: serviceId }),
        timeUnit: (state, { serviceId }) => getServiceTimeUnit(state, { id: serviceId }) || TimeUnit.Day,
        rateOptions: (state, { availabilityBlockId, serviceId }) =>
            availabilityBlockId
                ? // @ts-expect-error $TSFixMeError: Expected 1 arguments, but got 2.
                  getServiceRateOptions(state, { serviceId })
                : // @ts-expect-error $TSFixMeError: Expected 1 arguments, but got 2.
                  getBookableServiceRateOptions(state, { serviceId }),
        blockAdvancedFeaturesEnabled: isCurrentEnterpriseAvailabilityBlockAdvancedFeaturesEnabled,
        canCreateCustomers: isCurrentProfileEnabledToManageCustomers,
        canCreateCompanies: isCurrentProfileEnabledToManageCompanies,
        // @ts-expect-error $TSFixMeError: Type '(state: ApplicationState, { availabilityBlockId }: AvailabilityBlocksFormFieldsProps) => { creatorName: unknown; u...
        availabilityBlockMeta: (state, { availabilityBlockId }) =>
            availabilityBlockId ? getAvailabilityBlockFormattedMeta(state, { id: availabilityBlockId }) : undefined,
        availabilityBlockState: (state, { availabilityBlockId }) =>
            availabilityBlockId ? getAvailabilityBlock(state, { id: availabilityBlockId })?.state : null,
    }),
    (dispatch: CommanderThunkDispatch, { form }): DispatchProps => ({
        createNewBooker: ({ url, searchType }) =>
            dispatch(
                createNewSearchFormEntity({
                    url: enhanceCustomerCreateUrl(url),
                    searchType,
                    form,
                    scope: getBookerScope(form),
                    field: 'bookerId',
                })
            ),
        createCompany: ({ url, searchType }) =>
            dispatch(
                createNewSearchFormEntity({
                    url,
                    searchType,
                    form,
                    scope: getCompanyScope(form),
                    field: 'companyId',
                })
            ),
    })
)(AvailabilityBlockFormFields);
